import type React from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';

import { routes } from 'app/routes/constants';
import type { Lesson, ProgressivePlanInstance } from 'app/types/graphql';
import { lessonToWorkout } from 'app/utils/workouts';
import { trackEvent } from 'src/app/utils/analytics/amplitude/tracker';

import { RestDayWorkout } from 'app/pages/progressive-plan/components';
import TrainingPlanInstanceTracker from 'app/ui/components/molecules/training-plan-instance-tracker';
import Rail from 'app/ui/components/molecules/rail';
import type { Workout } from 'app/ui/components/molecules/workout-list';
import SimplifiedClassCard from 'app/ui/components/atoms/class-card/simplified';
import RestDayCard, { isRestDay } from 'app/ui/components/molecules/rest-day-card';
import { ClassCardContainer } from 'app/ui/components/atoms/class-card';

type RailWorkout = Workout & { to?: string };

export type ActiveTrainingPlanRailProps = {
  planInstance: ProgressivePlanInstance;
};

export type ToRailWorkoutArgs = {
  lesson: Lesson | null | undefined;
  label?: string;
  restId?: string;
};

const toRailWorkout = ({ lesson, label, restId }: ToRailWorkoutArgs): RailWorkout => {
  if (lesson) {
    return {
      ...lessonToWorkout(lesson, { label }),
      to: routes.WORKOUT.replace(':id', lesson.id),
    };
  }

  return {
    ...RestDayWorkout,
    ...(restId ? { id: restId } : {}),
    label,
  };
};

const RailWrapper = styled.div`
  width: 100%;
  & > div {
    margin-top: 0;
    margin-bottom: 0;
  }
`;

const StyledTracker = styled(TrainingPlanInstanceTracker)`
  flex-shrink: 0;
  width: 20rem;

  ${({ theme: { media } }) =>
    media.medium(
      () => `
    width: 25rem;
  `,
    )}

  ${({ theme: { media } }) =>
    media.large(
      () => `
    width: 30rem;
  `,
    )}
`;

const ActiveTrainingPlanRail: React.FC<ActiveTrainingPlanRailProps> = ({ planInstance }) => {
  const intl = useIntl();

  const workouts = useMemo(() => {
    const todayLabel = intl.formatMessage({ id: 'progressivePlan.today' });
    const upcomingWorkouts = planInstance.upcomingWorkouts ?? [];
    const todayLesson = planInstance.todaysWorkout?.progressivePlanWorkout?.lesson;
    const todayWorkout = toRailWorkout({ lesson: todayLesson, label: todayLabel });

    const upcomingItems = upcomingWorkouts.flatMap((upcoming, index) => {
      const ppw = upcoming?.progressivePlanWorkout;
      if (!ppw) {
        return [];
      }

      const label = index === 0 ? intl.formatMessage({ id: 'progressivePlan.tomorrow' }) : undefined;

      return [toRailWorkout({ lesson: ppw.lesson, label, restId: `rest-day-${index}` })];
    });

    return [todayWorkout, ...upcomingItems];
  }, [planInstance, intl]);

  return (
    <RailWrapper>
      <Rail showProgress={false}>
        <StyledTracker planInstance={planInstance} variant="small" />

        {workouts.map((workout, idx) =>
          isRestDay(workout.id) ? (
            <ClassCardContainer key={`${workout.id}-${idx}`}>
              <RestDayCard label={workout.label} testId={`active-plan-workout-${idx}`} />
            </ClassCardContainer>
          ) : (
            <ClassCardContainer key={`${workout.id}-${idx}`}>
              <SimplifiedClassCard
                srcSet={workout.srcSet}
                title={workout.title}
                to={workout.to}
                intensity={workout.intensity}
                equipment={workout.equipment}
                label={workout.label}
                isDisabled={workout.isDisabled}
                testId={`active-plan-workout-${idx}`}
                onClick={() =>
                  trackEvent('plan_workout_card_clicked', {
                    plan_id: planInstance.progressivePlan.id,
                    plan_title: planInstance.progressivePlan.name,
                    lesson_id: workout.id,
                    lesson_title: workout.title,
                  })
                }
              />
            </ClassCardContainer>
          ),
        )}
      </Rail>
    </RailWrapper>
  );
};

export default ActiveTrainingPlanRail;
